import React, { useState, useEffect } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { productService } from '../services/productService';
import { PaymentButton } from './PaymentButton';
import { paymentService } from '../services/paymentService';

export const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const data = await productService.getProducts({ in_stock: true, page_size: 50 });
        setProducts(data.products);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch products:', err);
        setError('Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const getProductTileClass = (productName) => {
    if (productName.includes('Berry')) return 'product-tile-berry';
    if (productName.includes('Citrus')) return 'product-tile-citrus';
    if (productName.includes('Green')) return 'product-tile-green';
    if (productName.includes('Tropical')) return 'product-tile-tropical';
    if (productName.includes('Midnight')) return 'product-tile-dark';
    if (productName.includes('Phoenix')) return 'product-tile-mixer';
    if (productName.includes('Tea')) return 'product-tile-tea';
    if (productName.includes('Mixer') || productName.includes('Mixed')) return 'product-tile-mixer';
    if (productName.includes('Tee') || productName.includes('Hoodie')) return 'product-tile-apparel';
    return 'bg-zinc-800';
  };

  return (
    <section id="products" className="py-24 bg-gradient-to-b from-black to-zinc-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-4">
            Shop the <span className="text-amber-400">Drops</span>
          </h2>
          <p className="text-xl text-gray-300">
            Bottles, pouches, and packs built for people who move different.
            Tap in once or make it a monthly ritual.
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-20">
            <p className="text-red-400 mb-4">{error}</p>
            <Button onClick={() => window.location.reload()} className="bg-amber-400 text-black hover:bg-amber-500">
              Retry
            </Button>
          </div>
        )}

        {/* Products Grid - Responsive for All Devices */}
        {!loading && !error && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
            {products.map((product) => (
              <Card
                key={product.id}
                className="bg-zinc-900 border-zinc-800 hover:border-amber-400/50 transition-all group relative overflow-hidden"
              >
                <div className="absolute top-2 right-2 z-10 flex flex-col gap-1 items-end">
                  {product.bestseller && (
                    <Badge className="bg-amber-400/90 text-black text-xs">
                      Bestseller
                    </Badge>
                  )}
                  {product.featured && !product.bestseller && (
                    <Badge className="bg-lime-400/90 text-black text-xs">
                      Featured
                    </Badge>
                  )}
                  {product.ageRestricted && (
                    <Badge className="bg-red-600 text-white text-xs font-bold">
                      21+
                    </Badge>
                  )}
                </div>
                
                {/* Product Image */}
                <div className={`relative overflow-hidden aspect-square ${getProductTileClass(product.name)}`}>
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300 product-image product-image-sharp"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>

                <CardContent className="p-3">
                  <p className="text-xs text-gray-400 mb-1">{product.category}</p>
                  <h3 className="text-sm font-bold text-white mb-2 line-clamp-2 group-hover:text-amber-400 transition-colors">
                    {product.name}
                  </h3>
                  <div className="mb-2">
                    <PaymentButton
                      packageId={paymentService.mapSkuToPackageId(product.id)}
                      productName={product.name}
                      price={product.price}
                      requiresAgeVerification={product.ageRestricted || false}
                      size="sm"
                    className="w-full bg-amber-400 hover:bg-amber-500 text-black font-bold"
                  >
                    Buy ${product.price}
                  </PaymentButton>
                </div>
                {product.size && (
                  <div className="text-xs text-gray-500">
                    {product.size}
                    {product.calories && ` • ${product.calories} cal`}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
        )}

        {/* Legal Disclaimer for Mixed Drinks */}
        <div className="max-w-4xl mx-auto mt-12 bg-zinc-900 border border-zinc-800 rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <div className="text-amber-400 text-2xl flex-shrink-0">⚠️</div>
            <div className="text-xs text-gray-400 leading-relaxed">
              <p className="font-semibold text-white mb-2">IMPORTANT LEGAL NOTICE - AGE RESTRICTION</p>
              <p className="mb-2">
                <strong className="text-white">Mixed Drinks:</strong> Products labeled as "Mixed Drink" may contain alcohol. 
                Purchase, possession, and consumption of alcoholic beverages are restricted to individuals 21 years of age or older. 
                Valid government-issued photo identification may be required at time of delivery.
              </p>
              <p className="mb-2">
                By purchasing mixed drink products, you certify that you are of legal drinking age in your jurisdiction. 
                Failure to comply with age verification requirements will result in order cancellation and no refund.
              </p>
              <p className="text-gray-500 text-xs">
                Please drink responsibly. Do not drink and drive. If you or someone you know has a drinking problem, 
                contact the SAMHSA National Helpline: 1-800-662-4357.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-400 mb-4">Can't decide? Start with a subscription and switch flavors anytime.</p>
          <Button
            size="lg"
            className="bg-white text-black hover:bg-amber-400 font-bold"
            onClick={() => document.getElementById('subscriptions').scrollIntoView({ behavior: 'smooth' })}
          >
            View Subscription Plans
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};